import Referees from 'components/general/Referees'
import { findArray, filterArray } from 'helpers/utils'
import Label from "components/general/Label"
import Table from 'react-bootstrap/Table';
import Competitor from 'components/presentation/Competitor';
import { NocMemo as Noc } from 'components/presentation/Noc';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStop, faMinusCircle, faPlusCircle, faCaretDown, faCaretRight } from "@fortawesome/free-solid-svg-icons";

import React, { useState, useEffect } from "react";
import Collapse from 'react-bootstrap/Collapse';
import { isLiveStatus } from "helpers/status";

import { Warnings, ActionTag } from "./FenCommon"
import { useViewport } from 'components/general/useViewPort';

import { CompetitionFromStore } from 'helpers/utils';


// finds a fencer into a team
const findRelayFencer = (team, code) => {
  return team?.find(i => i?.athlete?.code === code)
}

// finds the athlete with role substitute
const findSubstitute = (team) => {
  return team?.find(i => i?.eventUnitEntries?.find(eue => eue.eue_code === 'SUBSTITUTE' && eue.eue_value === 'S'))
}

// true if the fencer is the winner of the relay
const isWinner = (comp, pos) => {
  return comp?.extResults?.find(i => i.er_code === "BOUT" && i.er_pos === pos)?.extensions?.find(i => i.ere_code === 'WLT')?.ere_value === 'W'
}

// last relay number were a competitor participates in
const findLastRelay = (comp) => {
  const bouts = comp?.extResults?.filter(i => i.er_code === "BOUT")?.sort((a, b) => (b.er_pos + 0) - (a.er_pos + 0))
  if (bouts !== undefined) {
    return bouts[0]?.er_pos
  }
  return ''
}

// true is fencer is being replaced during a relay
const isReplaced = (comp) => {
  return comp?.extResults?.find(i => i.er_code === 'REPLACED')?.er_value === 'Y'
}

// renders a yellow or red or black card in extended data section
const findPenaltyCard = (action, comp) => {
  const className = action?.pbpa_Action === 'YC' || action?.pbpa_Action === 'P-y' ? 'text-warning' : action?.pbpa_Action === 'RC' || action?.pbpa_Action === 'P-r' ? 'text-danger' : ''
  const found = action?.competitors[0]?.athletes?.find(i => i?.pbpat_code === comp?.athlete?.code);
  if (action?.pbpa_Action && found) {
    return (<ActionTag className={className} action={action?.pbpa_Action} cardSide={'L'} />)
  }
  return '';
}

// find the highest penalty in a single relay: black card first, then red card, then yellow card  
const findHighestPenaltyCard = (actions, comp) => {
  let yellowCard = false
  let actionY = ''
  let redCard = false
  let actionR = ''
  let blackCard = false
  let actionB = ''
  let found = false;
  actions?.forEach(action => {
    if (action?.competitors[0]?.athletes?.find(i => i?.pbpat_code === comp?.athlete?.code)) {
      found = true;
      if (action?.pbpa_Action === 'YC' || action?.pbpa_Action === 'P-y') {
        yellowCard = true;
        actionY = action?.pbpa_Action
      }
      if (action?.pbpa_Action === 'RC' || action?.pbpa_Action === 'P-r') {
        redCard = true
        actionR = action?.pbpa_Action
      }
      if (action?.pbpa_Action === 'BC' || action?.pbpa_Action === 'P-b') {
        blackCard = true
        actionB = action?.pbpa_Action
      }
    }
  })
  const className = blackCard ? '' : redCard ? 'text-danger' : yellowCard ? 'text-warning' : ''
  const action = blackCard ? actionB : redCard ? actionR : yellowCard ? actionY : ''
  if (action && found) {
    return (<ActionTag className={className} action={action} cardSide={'L'} />)
  }
  return '';
}

const Relay = ({ teamA, teamB, item, actions, current, status, openAll, overtime }) => {
  const { breakPoint } = useViewport();
  // arrows management. hide/show actions
  const hasActions = actions ? true : false

  const competition = CompetitionFromStore();
  const iconClose = competition === 'EG2023' ? faMinusCircle : faCaretDown;
  const iconOpen = competition === 'EG2023' ? faPlusCircle : faCaretRight;

  const arrowDown = <FontAwesomeIcon icon={iconClose} />
  const arrowUp = <FontAwesomeIcon icon={iconOpen} />

  const [open, setOpen] = useState(false);
  const [arrow, setArrow] = useState(hasActions ? arrowUp : '');
  const isCurrentRelay = isLiveStatus(status) && current === `R${item?.ei_pos}`

  useEffect(() => {
    if (open) {
      setArrow(hasActions ? arrowDown : '')
    } else {
      setArrow(hasActions ? arrowUp : '')
    }
  }, [hasActions])

  useEffect(() => {
    if (openAll) {
      setArrow(hasActions ? arrowDown : '')
    } else {
      setArrow(hasActions ? arrowUp : '')
    }
    setOpen(openAll);
  }, [openAll])

  const handleClick = () => {
    if (open) {
      setArrow(hasActions ? arrowUp : '')
    } else {
      setArrow(hasActions ? arrowDown : '')
    }
    setOpen(!open);
  }

  // The two fencers participating in the relay: side A and side B
  // Needed to show the name of the fencer, results etc
  const compA = findRelayFencer(teamA, item?.extensions?.find(i => i.eie_code === 'A')?.eie_value)
  const compB = findRelayFencer(teamB, item?.extensions?.find(i => i.eie_code === 'B')?.eie_value)

  // true if A or B is the winner. Needed to bolding the name of the winner
  const isAWinner = isWinner(compA, item?.ei_pos)
  const isBWinner = isWinner(compB, item?.ei_pos)

  // last relay played for fencer A and B
  // needed when a fencer is replaced
  const lastRelayPlayedA = findLastRelay(compA)
  const lastRelayPlayedB = findLastRelay(compB)

  // find both sustitutes: sides A, B
  // needed to show substitute name when the fencer is replaces
  const substituteA = findSubstitute(teamA);
  const substituteB = findSubstitute(teamB);

  // true when a fencer is replaced by the substitute
  const isReplacedA = isReplaced(compA)
  const isReplacedB = isReplaced(compB)

  // rule to determine if it is needed to show the substitute below the replaced fencer
  // the rule is: the fencer is tagged as replaced (isReplacedX) and
  // because a fencer can play more than one relay: being in the same relay as the last relay played by the fencer (item?.ei_pos === lastRelayPlayedX)
  const showSubstituteA = item?.ei_pos === lastRelayPlayedA && isReplacedA
  const showSubstituteB = item?.ei_pos === lastRelayPlayedB && isReplacedB


  // show fencer name in side A and the substitute when proceed
  let fencerA = <Competitor hidePicture={true} hideNoc={true} showSubstitute={true} eventUnitEntries={compA?.eventUnitEntries} {...compA?.athlete} disciplineClass={compA?.disciplineClass} />
  if (showSubstituteA) {
    fencerA = (<>
      <div><Competitor hidePicture={true} hideNoc={true} showSubstitute={true} eventUnitEntries={compA?.eventUnitEntries} {...compA?.athlete} disciplineClass={compA?.disciplineClass} /></div>
      <div><Competitor hidePicture={true} hideNoc={true} showSubstitute={true} eventUnitEntries={substituteA?.eventUnitEntries} {...substituteA?.athlete} disciplineClass={compA?.disciplineClass} /></div>
    </>)
  }

  // show fencer name in side B and the substitute when proceed
  let fencerB = <Competitor hidePicture={true} hideNoc={true} showSubstitute={true} eventUnitEntries={compB?.eventUnitEntries} {...compB?.athlete} disciplineClass={compB?.disciplineClass} />
  if (showSubstituteB) {
    fencerB = (<>
      <div><Competitor hidePicture={true} hideNoc={true} showSubstitute={true} eventUnitEntries={compB?.eventUnitEntries} {...compB?.athlete} disciplineClass={compB?.disciplineClass} /></div>
      <div><Competitor hidePicture={true} hideNoc={true} showSubstitute={true} eventUnitEntries={substituteB?.eventUnitEntries} {...substituteB?.athlete} disciplineClass={compB?.disciplineClass} /></div>
    </>)
  }

  // scores 
  const scoreA = compA?.extResults?.find(i => i.er_pos === item?.ei_pos)?.extensions?.find(i => i.ere_code === 'SCORE')?.ere_value;
  const scoreB = compB?.extResults?.find(i => i.er_pos === item?.ei_pos)?.extensions?.find(i => i.ere_code === 'SCORE')?.ere_value;

  // results
  const resultA = compA?.extResults?.find(i => i.er_pos === item?.ei_pos)?.er_value;
  const resultB = compB?.extResults?.find(i => i.er_pos === item?.ei_pos)?.er_value;


  return (
    <>
      <tr className={isCurrentRelay ? 'currentIndicator' : ''}>
        {(breakPoint !== "sm" && breakPoint !== "xs") && <td>{item?.ei_pos}</td>}
        {(breakPoint !== "sm" && breakPoint !== "xs") && <td></td>}
        {(breakPoint !== "sm" && breakPoint !== "xs") && <td className="noc"><Noc {...compA?.athlete?.organisation} /></td>}
        <td className={isAWinner ? 'competitor-winner' : 'competitor'}>{fencerA}</td>
        <td>{findHighestPenaltyCard(actions, compA)}</td>
        <td className={'font-weight-bold'}>{scoreA}</td>
        <td className="result"><strong>{resultA}</strong></td>
        <td className="separator"><strong>{overtime ? 'OT' : '-'}</strong></td>
        <td className="result"><strong>{resultB}</strong></td>
        <td>{scoreB}</td>
        {(breakPoint !== "sm" && breakPoint !== "xs") && <td className="noc"><Noc {...compB?.athlete?.organisation} /></td>}
        <td className={isBWinner ? 'competitor-winner' : 'competitor'}>{fencerB}</td>
        <td>{findHighestPenaltyCard(actions, compB)}</td>
        <td className="arrow" onClick={(e) => handleClick()}>{arrow}</td>
      </tr>
      <Collapse in={open}>
        <tr>
          <td colSpan={14}>
            <Table className="expandible" striped>
              <tbody>
                {
                  actions?.map(
                    action => {
                      return (
                        <tr key={action?.pbpa_id}>
                          {(breakPoint !== "sm" && breakPoint !== "xs") && <td className="e-gap1" colSpan={2}></td>}
                          <td className="e-when">{action?.pbpa_When}</td>
                          <td className="e-cards" colSpan={2}>{findPenaltyCard(action, compA)}</td>
                          <td className="e-touches">{action?.pbpa_ScoreH}</td>
                          <td className="e-gap2" colSpan={3}></td>
                          <td className="e-touches">{action?.pbpa_ScoreA}</td>
                          <td className="e-cards" colSpan={2}>{findPenaltyCard(action, compB)}</td>
                          <td className="e-gap3" colSpan={2}></td>
                        </tr>
                      )
                    }
                  )
                }
              </tbody>
            </Table>
          </td>
        </tr>
      </Collapse>
    </>)
}

const FenTeams = ({ data }) => {
  const hasActions = data?.results?.playByPlay !== undefined && data?.results?.playByPlay[0]?.actions ? true : false

  const teamA = data?.results?.home?.teamAthletes;
  const teamB = data?.results?.away?.teamAthletes;

  const relays = filterArray(data?.results?.extendedInfos, { ei_code: ['COMP'] })?.sort((a, b) => (a.ei_pos + 0) - (b.ei_pos + 0))
  const currentRelay = findArray(data?.results?.extendedInfos, { ei_code: ['LAST_COMP'] })?.ei_pos
  const resultStatus = data?.results?.status?.code;

  const competition = CompetitionFromStore();
  const iconClose = competition === 'EG2023' ? faMinusCircle : faCaretDown;
  const iconOpen = competition === 'EG2023' ? faPlusCircle : faCaretRight;

  const arrowDown = <FontAwesomeIcon icon={iconClose} />
  const arrowUp = <FontAwesomeIcon icon={iconOpen} />

  const [open, setOpen] = useState(false);
  const [arrow, setArrow] = useState(hasActions ? arrowUp : '');

  const { breakPoint } = useViewport();

  // to show arrow in the header when there is almost one action
  useEffect(() => {
    if (open) {
      setArrow(hasActions ? arrowDown : '')
    } else {
      setArrow(hasActions ? arrowUp : '')
    }
  }, [hasActions])

  // managenent of header arrow when clicked
  const handleClick = () => {
    if (open) {
      setArrow(hasActions ? arrowUp : '')
    } else {
      setArrow(hasActions ? arrowDown : '')
    }
    setOpen(!open);
  }

  const lastIndex = relays?.length === undefined ? -1 : relays?.length - 1

  return (
    <div className="pbp-teams-main">
      <Table>
        <thead>
          <tr>
            {(breakPoint !== "sm" && breakPoint !== "xs") && <th className="relay"><Label data={'Relay'} /></th>}
            {(breakPoint !== "sm" && breakPoint !== "xs") && <th className="gap"></th>}
            <th className="fencer" colSpan={(breakPoint !== "sm" && breakPoint !== "xs") ? 2 : 1}><Label data="Fencer" /></th>
            <th className="cards"></th>
            <th className="touches"><Label data="Touches" /></th>
            <th className="score" colSpan={3}><Label data="Score" /></th>
            <th className="touches"><Label data="Touches" /></th>
            <th className="fencer" colSpan={(breakPoint !== "sm" && breakPoint !== "xs") ? 2 : 1}><Label data="Fencer" /></th>
            <th className="cards"></th>
            <th className="arrow" onClick={(e) => handleClick()}>{arrow}</th>
          </tr>
        </thead>
        <tbody>
          {relays?.map((relay, index) => {
            // actions showed in extended data
            const actions = data?.results?.playByPlay?.find(i => i.subcode === `R${relay?.ei_pos}`)?.actions;
            // needed to show 'OT' in the last Relay if proceed
            const overtime = index === lastIndex && findArray(data?.results?.extendedInfos, { ei_code: ['OVERTIME'] })?.ei_value === 'Y'
            // shows data of a single relay
            return <Relay teamA={teamA} teamB={teamB} item={relay} key={relay?.ei_pos} actions={actions} current={currentRelay} status={resultStatus} openAll={open} overtime={overtime} />
          }
          )
          }
        </tbody>
      </Table>
      <Warnings data={data} />
      {(breakPoint !== "sm" && breakPoint !== "xs") && <Referees data={data?.results?.officials} config={{ header: 'Referees', source: 'official' }} />}
    </div>
  )
}

export default FenTeams;